import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import ExternalPolicyUpdateModal from '../../components/ExternalPolicy/externalPolicyUpdateModal';
import {
    closeExternalPolicyUpdateModal,
    fetchExternalPolicyQuestions,
    changeExternalPolicyFormData,
    resetChildQuestions,
    addExternalPolicyAttachment,
    removeExternalPolicyAttachment,
    setExternalPolicyValidationError,
    submitExternalPolicyUpdate,
    clearExternalPolicyErrors,
} from '../../reducers/externalPolicyUpdate';
import { findUpdateQuestionById } from '../../helpers/updateTab/functions';
import { fileToObjectHandle, readFileAsBase64Async, validateAmount, isValidEmail } from '../../helpers/functions';
import { arrayUnique } from '../../helpers/questions/functions';
import { NAME_FIELD, POLICY_UPDATE_QUESTION_MN_FEE_EARNER_EMAIL, MOMENT_DATE_FORMAT } from '../../helpers/constants';
import { INPUT_TYPE } from '../../helpers/formGroupVariants';
import { DATE_REGURAL_EXPRESSION } from '../../helpers/variables';
import moment from 'moment';

const ExternalPolicyUpdateHOC = () => {
    const dispatch = useDispatch();

    const {
        isOpen,
        selectedPolicy,
        questions,
        fetching,
        fetchError,
        formData,
        attachments,
        validationError,
        submitting,
        submitted,
        submitError,
        successMessage,
    } = useSelector(state => state.externalPolicyUpdate);

    // Fetch questions when the modal opens
    useEffect(() => {
        if (isOpen) {
            dispatch(fetchExternalPolicyQuestions());
        }
    }, [isOpen, dispatch]);

    const handleClose = () => {
        dispatch(closeExternalPolicyUpdateModal());
    };

    const handleFormCompChange = (name, value) => {
        dispatch(changeExternalPolicyFormData({ key: name, value }));
    };

    const _findResetIds = (question, answer) => {
        let ids = [];
        _.forEach(question.options, o => {
            if (o.id.toString() !== answer.toString()) {
                _.forEach(o.childQuestions, cq => {
                    const matched = findUpdateQuestionById(questions, cq.id);
                    ids.push(matched.id);
                    ids = [...ids, ..._findResetIds(matched, answer)];
                });
            }
        });
        return ids;
    };

    const handleSelectChange = (evt) => {
        const changedFieldId = evt.target.name;
        const changedFieldValue = evt.target.value;
        const triggeredQuestion = findUpdateQuestionById(questions, changedFieldId);
        let resetIds = [];
        if (triggeredQuestion) {
            resetIds = _findResetIds(triggeredQuestion, changedFieldValue);
        }
        if (resetIds.length > 0) {
            dispatch(resetChildQuestions({ resetIds }));
        }
        dispatch(changeExternalPolicyFormData({ key: changedFieldId, value: changedFieldValue }));
    };

    const handleDateChange = (name, value, minDate, maxDate) => {
        if (!value) {
            // Manual keyboard input path
            if (name.target.value.length >= 8) {
                let split = name.target.value.split('/');
                if (split.length === 3) {
                    if (split[2].length === 4) {
                        if (!name.target.value.match(DATE_REGURAL_EXPRESSION)) {
                            dispatch(changeExternalPolicyFormData({ key: name.target.name, value: '' }));
                            return false;
                        } else {
                            if (minDate) {
                                if (moment(name.target.value, 'DD/MM/YYYY').isBefore(moment(minDate, 'DD/MM/YYYY'))) {
                                    dispatch(changeExternalPolicyFormData({ key: name.target.name, value: '' }));
                                    return false;
                                }
                            }
                            if (maxDate) {
                                if (moment(name.target.value, 'DD/MM/YYYY').isAfter(moment(maxDate, 'DD/MM/YYYY'))) {
                                    dispatch(changeExternalPolicyFormData({ key: name.target.name, value: '' }));
                                    return false;
                                }
                            }
                        }
                    }
                }
            }
            dispatch(changeExternalPolicyFormData({ key: name.target.name, value: name.target.value }));
        } else {
            dispatch(changeExternalPolicyFormData({ key: name, value }));
        }
    };

    const onDrop = (files) => {
        files.forEach(async file => {
            const alreadyExists = !!attachments.find(a => file.name === a.name);
            if (!alreadyExists) {
                let attachment = {
                    name: file.name,
                    encodedFile: '',
                    fileType: file.type
                };
                const base64String = await readFileAsBase64Async(file);
                attachment = { ...attachment, encodedFile: base64String };
                dispatch(addExternalPolicyAttachment(attachment));
            }
        });
    };

    const handleMultipleQuestionFiles = async (evt, questionId) => {
        const questionFiles = await Promise.all(evt.map(async (file) => {
            return await fileToObjectHandle(file);
        }));
        const existingFiles = formData[questionId] || [];
        const newValue = arrayUnique([...existingFiles, ...questionFiles], NAME_FIELD);
        dispatch(changeExternalPolicyFormData({ key: questionId, value: newValue }));
    };

    const handleSingleFileQuestions = async (evt, questionId) => {
        const questionFiles = await Promise.all(evt.map(async (file) => {
            return await fileToObjectHandle(file);
        }));
        dispatch(changeExternalPolicyFormData({ key: questionId, value: questionFiles }));
    };

    const handleRemoveAttachment = (attachment, index) => {
        dispatch(removeExternalPolicyAttachment(index));
    };


    const shouldUpdateQuestionBeMandatory = () => {
        return true;
    };

    const handleSubmit = () => {
        // Check at least one question has been answered
        const hasAnyAnswer = questions.some(q => {
            const val = formData[q.id];
            if (val === undefined || val === null || val === '') return false;
            if (Array.isArray(val) && val.length === 0) return false;
            return true;
        });

        if (!hasAnyAnswer) {
            dispatch(setExternalPolicyValidationError('Please answer at least one question before submitting.'));
            return;
        }

        // Validate email fields
        const emailQuestion = questions.find(q => q.machineName === POLICY_UPDATE_QUESTION_MN_FEE_EARNER_EMAIL);
        if (emailQuestion) {
            const emailValue = formData[emailQuestion.id];
            if (emailValue && !isValidEmail(emailValue)) {
                dispatch(setExternalPolicyValidationError('Email address is not valid.'));
                return;
            }
        }

        // Validate date fields
        const invalidDate = questions.find(q =>
            q.inputType === INPUT_TYPE.CALENDAR && formData[q.id] && !moment(formData[q.id]).isValid()
        );
        if (invalidDate) {
            dispatch(setExternalPolicyValidationError('Date input is not valid.'));
            return;
        }

        // Validate amount fields
        const invalidAmount = questions.find(q =>
            q.inputType === INPUT_TYPE.AMOUNT && formData[q.id] && validateAmount(formData[q.id], q.minimumValue)
        );
        if (invalidAmount) {
            dispatch(setExternalPolicyValidationError(`Please enter a valid amount for ${invalidAmount.name}`));
            return;
        }

        // Separate file-type questions from text answers
        const fileInputTypes = [INPUT_TYPE.UPLOAD, INPUT_TYPE.MULTI_UPLOAD];
        const fileQuestionIds = new Set(
            questions.filter(q => fileInputTypes.includes(q.inputType)).map(q => q.id.toString())
        );
        const filteredAnswers = {};
        const questionAttachments = [];
        Object.keys(formData).forEach(key => {
            if (fileQuestionIds.has(key.toString())) {
                const val = formData[key];
                if (Array.isArray(val)) {
                    val.forEach(file => questionAttachments.push(file));
                }
            } else {
                const val = formData[key];
                if (typeof val === 'string') {
                    filteredAnswers[key] = val;
                } else if (val instanceof Date || moment.isMoment(val)) {
                    filteredAnswers[key] = moment(val, MOMENT_DATE_FORMAT).format();
                }
            }
        });

        // Build payload and dispatch
        const payload = {
            policyNumber: selectedPolicy.policyNumber,
            solicitorReference: selectedPolicy.solicitorReference,
            clientName: selectedPolicy.clientName,
            answers: filteredAnswers,
            attachments: [...attachments, ...questionAttachments],
        };
        dispatch(submitExternalPolicyUpdate(payload));
    };

    const handleDismissError = () => {
        dispatch(clearExternalPolicyErrors());
    };

    const errorMessage = fetchError || submitError || validationError || '';

    // Early return — do not render if modal is not open
    if (!isOpen) return null;

    return (
        <ExternalPolicyUpdateModal
            isOpen={isOpen}
            onClose={handleClose}
            selectedPolicy={selectedPolicy}
            questions={questions}
            fetching={fetching}
            formData={formData}
            attachments={attachments}
            submitting={submitting}
            submitted={submitted}
            successMessage={successMessage}
            errorMessage={errorMessage}
            handleFormCompChange={handleFormCompChange}
            handleDateChange={handleDateChange}
            handleSelectChange={handleSelectChange}
            onDrop={onDrop}
            onDropMultiple={handleMultipleQuestionFiles}
            onDropSingle={handleSingleFileQuestions}
            handleRemoveAttachment={handleRemoveAttachment}
            shouldUpdateQuestionBeMandatory={shouldUpdateQuestionBeMandatory}
            handleSubmit={handleSubmit}
            handleDismissError={handleDismissError}
        />
    );
};

export default ExternalPolicyUpdateHOC;
